import _ from 'lodash';
import { Position } from 'app/agriculture/queries/types/geometry/Position';

/** For a given polygon and a center point, calculate the length of the line that runs east-west
    from the center point and intersects the polygon. Do the same for north-south, then return
    the smaller of those two lengths. */
export function calculateSmallestAxisDistance(polygon: Position[], pole: Position) {
    const [poleLon, poleLat] = pole;
    const intersectionsLon: Position[] = [];
    const intersectionsLat: Position[] = [];

    // Iterate over each edge of the polygon
    for (let i = 0; i < polygon.length; i++) {
        const p1 = polygon[i];
        const p2 = polygon[(i + 1) % polygon.length];

        if (p1 == null || p2 == null) throw new Error('Polygon needs at least 3 points');

        const [p1Lon, p1Lat] = p1;
        const [p2Lon, p2Lat] = p2;

        // Check if the edge intersects with the x-coordinate
        if ((p1Lat >= poleLat && p2Lat < poleLat) || (p1Lat < poleLat && p2Lat >= poleLat)) {
            const t = (poleLat - p1Lat) / (p2Lat - p1Lat);
            const y = p1Lon + t * (p2Lon - p1Lon);
            intersectionsLon.push([y, poleLat]);
        }

        // Check if the edge intersects with the y-coordinate
        if ((p1Lon >= poleLon && p2Lon < poleLon) || (p1Lon < poleLon && p2Lon >= poleLon)) {
            const t = (poleLon - p1Lon) / (p2Lon - p1Lon);
            const x = p1Lat + t * (p2Lat - p1Lat);
            intersectionsLat.push([poleLon, x]);
        }
    }

    // get the biggest and smallest intersection polygon
    const latitudes = intersectionsLat.map(([, lat]) => lat);
    const maxLat = Math.max(...latitudes);
    const minLat = Math.min(...latitudes);
    const [latPoint1, latPoint2] = intersectionsLat.filter(
        ([, lat]) => lat == maxLat || lat == minLat,
    );
    const longitudes = intersectionsLon.map(([lon]) => lon);
    const maxLon = Math.max(...longitudes);
    const minLon = Math.min(...longitudes);
    const [lonPoint1, lonPoint2] = intersectionsLon.filter(
        ([lon]) => lon == maxLon || lon == minLon,
    );

    if (latPoint1 == null || latPoint2 == null || lonPoint1 == null || lonPoint2 == null) {
        return 0;
    }

    const distances: number[] = [];

    // return the smallest distance between intersection polygon
    distances.push(
        Cesium.Cartesian3.distance(
            Cesium.Cartesian3.fromDegrees(...latPoint1),
            Cesium.Cartesian3.fromDegrees(...latPoint2),
        ),
    );
    distances.push(
        Cesium.Cartesian3.distance(
            Cesium.Cartesian3.fromDegrees(...lonPoint1),
            Cesium.Cartesian3.fromDegrees(...lonPoint2),
        ),
    );

    return _.min(distances) ?? 0;
}
